import { useState, useRef, useCallback, useEffect } from "react";
import { P, FF } from "../../styles/tokens";
import { api } from "../../services/api";
import { fmt } from "../../utils/format";
import { catIcon, catColor } from "../../constants/categories";
import { track } from "../../lib/posthog";

/**
 * AudioRecorder — Componente para registrar gastos/ingresos por voz.
 * Graba audio con MediaRecorder, lo envía al backend (Gemini),
 * muestra las transacciones extraídas y permite confirmarlas.
 *
 * Capa A: audio constraints (noise suppression, auto gain), trackeo de volumen máximo
 *         para rechazar audios muy quedos antes de subir a Gemini.
 * Capa C: muestra transcripción cuando falla, fallback manual con concepto pre-llenado, badge de revisión.
 */
export default function AudioRecorder({ onConfirm, onClose, mobile }) {
  const [state, setState] = useState("idle"); // idle | recording | processing | results | error
  const [seconds, setSeconds] = useState(0);
  const [results, setResults] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(new Set());
  const [transcripcion, setTranscripcion] = useState("");
  const mediaRef = useRef(null);
  const streamRef = useRef(null); // stream persistente — evita re-pedir permiso de mic en cada grabación (importante en iOS PWA)
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const autoStopRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const maxVolRef = useRef(0);

  const MAX_SECONDS = 20;
  const MIN_VOLUME_THRESHOLD = 0.04; // bajo este nivel, se considera "muy bajito"

  // Cleanup al desmontar — libera el micrófono cuando se cierra el panel
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch {}
      }
      if (streamRef.current) {
        try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
        streamRef.current = null;
      }
    };
  }, []);

  // Reutiliza el stream si ya existe (evita prompt de permiso repetido).
  // Si los tracks fueron detenidos externamente, pide uno nuevo.
  const getOrRequestStream = useCallback(async () => {
    const existing = streamRef.current;
    if (existing && existing.getTracks().some((t) => t.readyState === "live")) {
      return existing;
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 48000,
      },
    });
    streamRef.current = stream;
    return stream;
  }, []);

  const startRecording = useCallback(async () => {
    try {
      try { track("voz_grabacion_iniciada"); } catch {}
      // A1: audio constraints — noise suppression, auto gain, echo cancellation
      const stream = await getOrRequestStream();

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm",
      });
      mediaRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      // AnalyserNode para trackear el volumen máximo (sin UI visible)
      // Se usa al detener para rechazar audios muy quedos antes de subir a Gemini
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioCtx();
        audioCtxRef.current = audioCtx;
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        maxVolRef.current = 0;

        const tick = () => {
          if (!analyserRef.current) return;
          analyser.getByteTimeDomainData(dataArray);
          // Calcular RMS (volumen promedio)
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            const v = (dataArray[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / dataArray.length);
          if (rms > maxVolRef.current) maxVolRef.current = rms;
          rafRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch (e) {
        console.warn("AnalyserNode no disponible:", e);
      }

      mediaRecorder.start(250); // chunks cada 250ms
      setState("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);

      // Auto-stop a los 20 segundos
      autoStopRef.current = setTimeout(() => {
        stopAndProcess();
      }, MAX_SECONDS * 1000);
    } catch (err) {
      setErrorMsg("No se pudo acceder al micrófono. Verifica los permisos.");
      setState("error");
    }
  }, [getOrRequestStream]);

  const stopAndProcess = useCallback(async () => {
    if (!mediaRef.current || mediaRef.current.state !== "recording") return;

    clearInterval(timerRef.current);
    clearTimeout(autoStopRef.current);

    // Detener visualizer
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const peakVolume = maxVolRef.current;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch {}
      audioCtxRef.current = null;
      analyserRef.current = null;
    }

    return new Promise((resolve) => {
      mediaRef.current.onstop = async () => {
        // No detenemos los tracks del stream — se reutiliza en grabaciones futuras.
        // El cleanup del useEffect libera el micrófono al desmontar el panel.
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });

        if (blob.size < 1000) {
          setErrorMsg("La grabación es muy corta. Intenta de nuevo.");
          setState("error");
          resolve();
          return;
        }

        // A2: rechazar si el volumen máximo fue muy bajo
        if (peakVolume > 0 && peakVolume < MIN_VOLUME_THRESHOLD) {
          setErrorMsg("Hablaste muy bajito y casi no se escucha. Acércate al micrófono e intenta de nuevo.");
          setState("error");
          resolve();
          return;
        }

        setState("processing");

        try {
          const data = await api.ingestas.audio(blob);
          const txs = data.transacciones || [];
          const transcript = data.transcripcion || "";
          setTranscripcion(transcript);

          if (txs.length === 0) {
            // C1: mostrar la transcripción si existe
            setErrorMsg(
              transcript
                ? `Escuché: "${transcript.slice(0, 200)}". No pude extraer una transacción de ahí.`
                : "No pude entender la grabación. Intenta hablar más claro."
            );
            setState("error");
            try { track("voz_procesada", { transacciones_extraidas: 0, exitoso: false }); } catch {}
          } else {
            setResults(txs);
            // Seleccionar todas por defecto
            setSelectedIdx(new Set(txs.map((_, i) => i)));
            setState("results");
            try { track("voz_procesada", { transacciones_extraidas: txs.length, exitoso: true }); } catch {}
          }
        } catch (err) {
          const isTokenLimit = err.message?.includes("límite de uso");
          setErrorMsg(isTokenLimit
            ? "Has alcanzado tu límite de uso este mes 🐿️ Se renueva el próximo mes."
            : "Hubo un error al procesar el audio. Intenta de nuevo en unos segundos.");
          setState("error");
          try { track("voz_procesada", { transacciones_extraidas: 0, exitoso: false, error: isTokenLimit ? "rate_limit" : "backend_error" }); } catch {}
        }
        resolve();
      };

      mediaRef.current.stop();
    });
  }, []);

  const toggleSelect = (idx) => {
    setSelectedIdx((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const confirmResults = () => {
    const selected = results.filter((_, i) => selectedIdx.has(i));
    if (selected.length > 0 && onConfirm) onConfirm(selected);
    if (onClose) onClose();
  };

  // C2: fallback manual con la transcripción como concepto
  const addManualFromTranscript = () => {
    if (!onConfirm) return;
    const concepto = transcripcion ? transcripcion.slice(0, 80) : "Sin descripción";
    onConfirm([{
      concepto,
      monto: 0,
      categoria: "Otros",
      tipo: "gasto_extra",
      cuenta_id: null,
      fecha: null,
      necesita_revision: true,
    }]);
    if (onClose) onClose();
  };

  const retry = () => {
    setState("idle");
    setErrorMsg("");
    setResults([]);
    setSeconds(0);
    setTranscripcion("");
  };

  const formatTime = (s) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const tipoLabel = (tipo) =>
    tipo === "ingreso" ? "Ingreso" : tipo === "gasto_fijo" ? "Gasto fijo" : tipo === "transferencia" ? "🔄 Transferencia" : "Gasto extra";
  const tipoToken = (tipo) =>
    tipo === "ingreso" ? P.ingresos : tipo === "gasto_fijo" ? P.fijos : tipo === "transferencia" ? P.ahorro : P.extras;

  return (
    <div
      style={{
        background: P.surface,
        borderRadius: 20,
        border: `1.5px solid ${P.border}`,
        padding: mobile ? 20 : 24,
        maxWidth: 480,
        width: "100%",
        boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              background: P.ingresos.pastel,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
            }}
          >
            🎙️
          </div>
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 800,
                color: P.text,
                fontFamily: FF,
              }}
            >
              Registro por voz
            </div>
            <div
              style={{
                fontSize: 11,
                color: P.textXs,
                fontFamily: FF,
              }}
            >
              Dicta tus gastos o ingresos
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            width: 30,
            height: 30,
            borderRadius: 10,
            border: `1.5px solid ${P.border}`,
            background: "transparent",
            color: P.textXs,
            cursor: "pointer",
            fontSize: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ×
        </button>
      </div>

      {/* ── IDLE ── */}
      {state === "idle" && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div
            style={{
              fontSize: 13,
              color: P.textSm,
              fontFamily: FF,
              marginBottom: 20,
              lineHeight: 1.6,
            }}
          >
            Presiona el botón y dicta algo como:
            <br />
            <strong style={{ color: P.text }}>
              "Gasté trescientos en el súper y cien en gasolina"
            </strong>
          </div>
          <button
            onClick={startRecording}
            style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              border: "none",
              background: "linear-gradient(145deg, #EF4444, #DC2626)",
              color: "#fff",
              fontSize: 32,
              cursor: "pointer",
              boxShadow: "0 6px 24px rgba(239,68,68,0.35)",
              transition: "transform 0.15s",
            }}
          >
            🎙️
          </button>
          <div
            style={{
              fontSize: 11,
              color: P.textXs,
              fontFamily: FF,
              marginTop: 12,
            }}
          >
            Toca para grabar
          </div>
        </div>
      )}

      {/* ── RECORDING ── */}
      {state === "recording" && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#EF4444",
                animation: "pulse 1s infinite",
              }}
            />
            <span
              style={{
                fontSize: 28,
                fontWeight: 900,
                color: P.text,
                fontFamily: FF,
                letterSpacing: "-1px",
              }}
            >
              {formatTime(seconds)}
            </span>
          </div>
          {seconds >= MAX_SECONDS - 5 && (
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#EF4444",
                fontFamily: FF,
                marginBottom: 8,
              }}
            >
              {MAX_SECONDS - seconds > 0 ? `${MAX_SECONDS - seconds}s` : "Deteniendo..."}
            </div>
          )}
          <div
            style={{
              fontSize: 13,
              color: P.textSm,
              fontFamily: FF,
              marginBottom: 20,
            }}
          >
            Escuchando... dicta tus gastos o ingresos
          </div>
          <button
            onClick={stopAndProcess}
            style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              border: "none",
              background: P.text,
              color: "#fff",
              fontSize: 24,
              cursor: "pointer",
              boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
            }}
          >
            ⏹️
          </button>
          <div
            style={{
              fontSize: 11,
              color: P.textXs,
              fontFamily: FF,
              marginTop: 12,
            }}
          >
            Toca para detener
          </div>
          <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }`}</style>
        </div>
      )}

      {/* ── PROCESSING ── */}
      {state === "processing" && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <div style={{ fontSize: 36, marginBottom: 16 }}>🧠</div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: P.text,
              fontFamily: FF,
              marginBottom: 8,
            }}
          >
            Procesando audio...
          </div>
          <div
            style={{
              fontSize: 12,
              color: P.textXs,
              fontFamily: FF,
            }}
          >
            Gemini está extrayendo tus transacciones
          </div>
          <div
            style={{
              marginTop: 20,
              height: 4,
              background: P.border,
              borderRadius: 99,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                background: P.ingresos.mid,
                borderRadius: 99,
                width: "60%",
                animation: "loading 1.5s ease-in-out infinite",
              }}
            />
          </div>
          <style>{`@keyframes loading { 0% { width:10%; margin-left:0; } 50% { width:60%; margin-left:20%; } 100% { width:10%; margin-left:90%; } }`}</style>
        </div>
      )}

      {/* ── RESULTS ── */}
      {state === "results" && (
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: P.ingresos.text,
              fontFamily: FF,
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>✓</span> {results.length} transacción
            {results.length !== 1 ? "es" : ""} detectada
            {results.length !== 1 ? "s" : ""}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
            {results.map((tx, i) => {
              const t = tipoToken(tx.tipo);
              const selected = selectedIdx.has(i);
              return (
                <div
                  key={i}
                  onClick={() => toggleSelect(i)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 14px",
                    borderRadius: 14,
                    border: `1.5px solid ${selected ? t.base : P.border}`,
                    background: selected ? t.pastel : P.bg,
                    cursor: "pointer",
                    transition: "all 0.15s",
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      border: `2px solid ${selected ? t.base : P.border}`,
                      background: selected ? t.base : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 900,
                      flexShrink: 0,
                    }}
                  >
                    {selected && "✓"}
                  </div>
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 10,
                      background: `${catColor(tx.categoria)}18`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 16,
                      flexShrink: 0,
                    }}
                  >
                    {catIcon(tx.categoria)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: P.text,
                        fontFamily: FF,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      {tx.concepto}
                      {tx.necesita_revision && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: "#92400E",
                            background: "#FEF3C7",
                            border: "1px solid #FDE68A",
                            padding: "2px 6px",
                            borderRadius: 99,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Revisa
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: t.text,
                        fontFamily: FF,
                        fontWeight: 600,
                      }}
                    >
                      {tipoLabel(tx.tipo)} · {tx.categoria}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 900,
                      color: tx.tipo === "ingreso" ? P.ingresos.text : P.text,
                      fontFamily: FF,
                      flexShrink: 0,
                    }}
                  >
                    {tx.tipo === "ingreso" ? "+" : ""}
                    {fmt(tx.monto)}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={confirmResults}
              disabled={selectedIdx.size === 0}
              style={{
                flex: 1,
                padding: "12px 20px",
                borderRadius: 14,
                border: "none",
                background: selectedIdx.size > 0 ? P.ingresos.base : P.border,
                color: selectedIdx.size > 0 ? "#fff" : P.textXs,
                fontSize: 14,
                fontWeight: 800,
                fontFamily: FF,
                cursor: selectedIdx.size > 0 ? "pointer" : "default",
              }}
            >
              Agregar {selectedIdx.size > 0 ? `(${selectedIdx.size})` : ""}
            </button>
            <button
              onClick={retry}
              style={{
                padding: "12px 16px",
                borderRadius: 14,
                border: `1.5px solid ${P.border}`,
                background: "transparent",
                color: P.textSm,
                fontSize: 13,
                fontWeight: 600,
                fontFamily: FF,
                cursor: "pointer",
              }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* ── ERROR ── */}
      {state === "error" && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>😕</div>
          <div
            style={{
              fontSize: 13,
              color: P.textSm,
              fontFamily: FF,
              marginBottom: transcripcion ? 14 : 20,
              lineHeight: 1.6,
            }}
          >
            {errorMsg}
          </div>

          {/* C2: Botón de fallback manual cuando hay transcripción */}
          {transcripcion && (
            <button
              onClick={addManualFromTranscript}
              style={{
                display: "block",
                margin: "0 auto 16px",
                padding: "10px 18px",
                borderRadius: 12,
                border: `1.5px solid ${P.ingresos.tint}`,
                background: P.ingresos.pastel,
                color: P.ingresos.text,
                fontSize: 12,
                fontWeight: 700,
                fontFamily: FF,
                cursor: "pointer",
              }}
            >
              ✏️ Agregarlo manual y completar después
            </button>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button
              onClick={retry}
              style={{
                padding: "10px 24px",
                borderRadius: 12,
                border: "none",
                background: P.text,
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                fontFamily: FF,
                cursor: "pointer",
              }}
            >
              Intentar de nuevo
            </button>
            <button
              onClick={onClose}
              style={{
                padding: "10px 16px",
                borderRadius: 12,
                border: `1.5px solid ${P.border}`,
                background: "transparent",
                color: P.textSm,
                fontSize: 12,
                fontWeight: 500,
                fontFamily: FF,
                cursor: "pointer",
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
